/**
 * DignityChat (/chat) — "Connect your AI" consumer guide.
 *
 * The on-site chat is intentionally disconnected (no hosted API keys in this
 * stack — Master 2026-07-13). This page is the consumer-facing answer: the
 * whole page is a setup FAQ for connecting YOUR Claude / ChatGPT to the AHP
 * MCP server, with example conversations (real verified numbers) showing
 * what it unlocks. /mcp remains the technical reference; this page is the
 * two-minute version in Dignity's voice.
 *
 * The previous credit-based chat UI lives in git history (ChatSidebar /
 * ChatMessageArea components are untouched) if a hosted backend ever ships.
 */

import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Bot, Check, ChevronDown, Copy, MessageCircle, Sparkles, Terminal, Zap,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useToast } from '@/hooks/use-toast';
import { useBrand } from '@/config/brands';

const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans = "'Instrument Sans', system-ui, -apple-system, sans-serif";
const mono = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const MCP_URL = 'https://mcp.americanhospitalprices.com/mcp';

function useT() {
  const { isDark } = useTheme();
  return useMemo(() => {
    if (isDark) {
      return {
        bg: '#0a1628', text: '#FAF9F6',
        muted: 'rgba(250,249,246,0.62)', gold: '#C5A55A', red: '#e85d6c',
        navy: '#5B9BD5', green: '#5fbf8f',
        cardBg: '#0d1e35', border: 'rgba(250,249,246,0.10)',
        codeBg: 'rgba(250,249,246,0.06)', userBubble: '#123057',
        aiBubble: '#0d1e35',
      };
    }
    return {
      bg: '#FAF9F6', text: '#1B1B1B',
      muted: 'rgba(27,27,27,0.62)', gold: '#8a6f2f', red: '#B22234',
      navy: '#002868', green: '#0F8A5F',
      cardBg: '#ffffff', border: 'rgba(27,27,27,0.10)',
      codeBg: 'rgba(27,27,27,0.05)', userBubble: '#eaf0fa',
      aiBubble: '#ffffff',
    };
  }, [isDark]);
}

/* Example conversations — every number verified against the live data
   (launch audit round 29; re-verified vs scenario_costs_cache 2026-07-14).
   Do not invent prices here. */
/* Rich mock responses: `table` renders a Claude-style comparison table,
   `bars` a horizontal cost chart — mirroring what a connected Claude actually
   produces for these asks (it renders markdown tables natively and charts via
   artifacts). Every figure from scenario_costs_cache. */
type ExampleBar = { label: string; value: number; display: string; delta?: string };
type Example = {
  q: string;
  a: string;
  table?: { headers: string[]; rows: string[][] };
  bars?: ExampleBar[];
  note?: string;
};

const EXAMPLES: Example[] = [
  {
    q: 'I broke my leg. Build me a full breakdown of ALL my treatment options and what each would cost.',
    a: 'I searched the 12,000+ priced scenarios. Here’s your realistic option space — each covers the whole episode: ER visit, X-rays, the procedure, anesthesia, and follow-ups:',
    table: {
      headers: ['Treatment path', 'National avg (published)', 'Episode includes'],
      rows: [
        ['Closed reduction + casting', '$17,800', 'ER · imaging · reduction · cast · follow-ups'],
        ['Tibial shaft — surgical (IM nail)', '$30,000', '+ surgery · anesthesia · hardware · rehab'],
        ['Femur repair (ORIF with nail)', '$17,100', '+ surgery · anesthesia · hardware'],
        ['Child (2–5) — spica cast, non-op', '$13,800', 'ER · imaging · sedated casting'],
      ],
    },
    note: 'Give me your ZIP and I’ll rank the cheapest graded hospitals near you for each path, with links to every hospital’s own published file — or ask for a slide deck version to take to your appointment.',
  },
  {
    q: 'We’re expecting. What if something goes wrong — can you chart the costs?',
    a: 'Yes — the catalog prices complication variants alongside every base scenario. Here’s the published-charge picture, charted:',
    bars: [
      { label: 'Uncomplicated vaginal delivery', value: 32900, display: '$32.9k' },
      { label: 'With preeclampsia', value: 56600, display: '$56.6k', delta: '+72%' },
      { label: 'With postpartum hemorrhage', value: 61700, display: '$61.7k', delta: '+87%' },
    ],
    note: 'Each bar is a full scenario — prenatal visits through delivery and postpartum stay — and links back to its page with the itemized code-by-code breakdown and the hospitals that price it lowest near you.',
  },
  {
    q: 'What does a knee MRI actually cost at US hospitals?',
    a: 'Nationally, hospitals publish a median sticker price of about $3,672 for a lower-extremity joint MRI (CPT 73721) — but the spread is the real story: the average is $4,599 and individual hospitals publish anywhere from a few hundred dollars to five figures for the same scan. Want me to find the cheapest published prices near you?',
  },
  {
    q: 'Is $509 fair for a basic office visit?',
    a: 'That’s right at the national average. For an established-patient office visit (CPT 99213), 3,670 hospitals publish prices — the median is $308 and the average $509. If you’re paying cash, anything near $300 is a reasonable ask; several hospitals near you likely publish less.',
  },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: 'Is this free?',
    a: 'Yes. The data connection is free, needs no account with us, and no API key. Your existing AI subscription (Claude Pro/Max, ChatGPT with connectors) does the reasoning — we serve the data. The server is rate-limited to 120 requests a minute per connection, which a normal conversation never approaches.',
  },
  {
    q: 'What AI do I need?',
    a: 'Claude (claude.ai on web, mobile, or desktop — custom connectors are available on paid plans) or ChatGPT (a plan with connector / developer-mode support). Claude Code works too with a one-line command. If your AI supports the Model Context Protocol (MCP), it can connect.',
  },
  {
    q: 'What exactly does it unlock?',
    a: 'Fifteen tools over the full dataset: 546M+ published prices across 9,500+ US hospitals, 12,000+ fully-priced medical scenarios including complication variants (your AI searches these first — they bundle a whole episode of care the way a real bill arrives), national averages for 14,000+ CPT / 9,000+ HCPCS / 780+ MS-DRG / 500+ revenue codes plus 100,000+ drug (NDC) and 400,000+ supply prices, hospital letter grades and rankings, a bill-audit tool, cheapest-hospital search, and cross-country comparisons against 32 countries’ published tariffs.',
  },
  {
    q: 'Are these real prices?',
    a: 'They are gross charges — the sticker prices from each hospital’s own published chargemaster, which hospitals must disclose under the federal price-transparency rule. They’re what uninsured and out-of-network patients are actually billed. They are not insurance-negotiated rates, and your out-of-pocket cost with insurance will differ.',
  },
  {
    q: 'Can you see my conversations?',
    a: 'No. Your conversation happens entirely inside your AI provider (Anthropic or OpenAI). When your AI needs a price, it calls our data server with just the lookup — a code, a state, a hospital name. We never receive or store your chat.',
  },
  {
    q: 'Can it help with a bill I already got?',
    a: 'Yes — ask your AI to audit it. Read it the billing codes and amounts, and it will compare each line to the national median and flag outliers. For the phone call afterward, the free Negotiation Sheet on this site (Family planner → Create Custom Scenario) prints your local comps: the cheapest published prices for the same codes near you.',
  },
  {
    q: 'How current and how accurate is the data?',
    a: 'Hospitals republish chargemasters on their own schedules; we re-ingest continuously and re-aggregate the national averages after each wave. Every hospital answer links back to that hospital’s page on americanhospitalprices.com so you can verify the underlying rows yourself. Extreme single-line outliers can be errors in a hospital’s own file — the median is the robust number, and the tools are built to lead with it.',
  },
  {
    q: 'What’s the optional "skill" download?',
    a: 'The connector gives your AI the data; the skill teaches it the craft — lead with medians, use the exact grade scale, run the bill-audit and negotiation-comps workflows, and cite sources. Claude users can upload it under Settings → Capabilities → Skills. It’s optional: the connector alone works fine.',
  },
  {
    q: 'Something didn’t work — what do I check?',
    a: 'First: the URL must be exactly the /mcp path shown above, added as a custom connector (not a website link). Second: connector support requires a paid AI plan on most providers. Third: if you hit "rate limit exceeded," wait thirty seconds — a normal conversation won’t trigger it. Still stuck? Use the contact page and tell us which AI and which step failed.',
  },
  {
    // {advisor} is substituted at render — Dignity on AHP, the brand's own
    // short name on the siblings, since Dignity belongs to AHP alone.
    q: 'Why doesn’t {advisor} just chat right here?',
    a: 'Because you’d be paying us to run an AI you already pay for. Connecting your own assistant is free for you, keeps your conversation private with your AI provider, and lets us put everything into the data instead of inference bills. {advisor}’s voice still guides the site — and your AI, once connected, has every number {advisor} has.',
  },
];

export default function DignityChat() {
  const brand = useBrand();
  const T = useT();
  const { toast } = useToast();
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [copied, setCopied] = useState(false);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(MCP_URL);
      setCopied(true);
      toast({ title: 'Copied', description: 'Server URL copied to clipboard.' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Copy failed', description: MCP_URL });
    }
  };

  const eyebrow: React.CSSProperties = {
    fontFamily: sans, fontWeight: 700, fontSize: '0.72rem',
    letterSpacing: '0.1em', textTransform: 'uppercase', color: T.gold,
  };
  const h2: React.CSSProperties = {
    fontFamily: serif, fontWeight: 400, fontSize: 'clamp(1.6rem, 3vw, 2.2rem)',
    color: T.text, margin: '0.35rem 0 0',
  };
  const card: React.CSSProperties = {
    background: T.cardBg, border: `1px solid ${T.border}`, borderRadius: 14,
  };

  return (
    <div style={{ background: T.bg, minHeight: '100vh' }}>
      <Helmet>
        <title>Connect Your AI — Chat With Every US Hospital Price | {brand.name}</title>
        <meta
          name="description"
          content="Add one free connector and your Claude or ChatGPT can answer with real published prices from 9,500+ US hospitals — bill audits, cheapest-hospital search, full scenario costs. No account, no API key."
        />
      </Helmet>

      <div className="mx-auto px-5" style={{ maxWidth: 880, paddingTop: 48, paddingBottom: 96 }}>
        {/* ── HERO ─────────────────────────────────────────────────── */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          {/* Dignity is AHP's mascot — siblings get their own name in the slot. */}
          <p style={eyebrow}>{(brand.key === 'ahp' ? 'Dignity' : brand.shortName) + ' · your healthcare cost advisor'}</p>
          <h1 style={{ fontFamily: serif, fontWeight: 400, fontSize: 'clamp(2.1rem, 5vw, 3.1rem)', color: T.text, margin: '0.4rem 0 0.75rem', lineHeight: 1.12 }}>
            {'I live inside your AI now.'}
          </h1>
          <p style={{ fontFamily: sans, fontSize: '1.02rem', color: T.muted, lineHeight: 1.7, maxWidth: 640, margin: '0 auto' }}>
            {'You already pay for Claude or ChatGPT. Add one free connector and it can answer with every published hospital price in America — audit your bills, find the cheapest hospital near you, price whole medical journeys. No account. No API key. Two minutes.'}
          </p>

          {/* Copy-URL pill */}
          <button
            onClick={copyUrl}
            className="max-w-full"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 22,
              background: T.codeBg, border: `1px solid ${T.border}`, borderRadius: 999,
              padding: '0.65rem 1.15rem', cursor: 'pointer',
              fontFamily: mono, fontSize: '0.82rem', color: T.text,
              overflow: 'hidden',
            }}
            aria-label="Copy the MCP server URL"
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{MCP_URL}</span>
            {copied ? <Check size={15} color={T.green} style={{ flexShrink: 0 }} /> : <Copy size={15} color={T.gold} style={{ flexShrink: 0 }} />}
          </button>
          <p style={{ fontFamily: sans, fontSize: '0.75rem', color: T.muted, marginTop: 8 }}>
            {'That’s the whole secret — copy it, add it as a connector, start asking.'}
          </p>
        </div>

        {/* ── WHAT IT UNLOCKS: EXAMPLE CONVERSATIONS ───────────────── */}
        <section style={{ marginBottom: 48 }}>
          <p style={eyebrow}>{'What it unlocks'}</p>
          <h2 style={h2}>{'Ask like a person. Get answers with receipts.'}</h2>
          <p style={{ fontFamily: sans, fontSize: '0.9rem', color: T.muted, margin: '0.5rem 0 1.4rem' }}>
            {'Not just numbers — your AI searches 12,000+ full medical scenarios (complications included) and builds comparison tables, cost charts, even slide decks. Real figures from the live data; every answer links back to the hospital’s own page for verification.'}
          </p>

          <div style={{ display: 'grid', gap: 14 }}>
            {EXAMPLES.map((ex, i) => (
              <div key={i} style={{ ...card, padding: '1.1rem 1.2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                  <div style={{
                    background: T.userBubble, borderRadius: '14px 14px 4px 14px',
                    padding: '0.6rem 0.9rem', maxWidth: '80%',
                    fontFamily: sans, fontSize: '0.9rem', color: T.text,
                  }}>
                    {ex.q}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%', background: T.navy,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2,
                  }}>
                    <Bot size={14} color="#fff" />
                  </div>
                  <div style={{
                    background: T.aiBubble, border: `1px solid ${T.border}`,
                    borderRadius: '14px 14px 14px 4px', padding: '0.6rem 0.9rem',
                    fontFamily: sans, fontSize: '0.9rem', color: T.text, lineHeight: 1.65,
                    minWidth: 0, flex: 1,
                  }}>
                    {ex.a}

                    {/* Claude-style comparison table (options breakdown) */}
                    {ex.table && (
                      <div style={{ overflowX: 'auto', margin: '0.7rem 0 0.2rem' }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8rem' }}>
                          <thead>
                            <tr>
                              {ex.table.headers.map((h, hi) => (
                                <th key={hi} style={{
                                  textAlign: 'left', padding: '0.35rem 0.6rem',
                                  borderBottom: `2px solid ${T.gold}`,
                                  color: T.gold, fontWeight: 700, fontSize: '0.7rem',
                                  letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap',
                                }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {ex.table.rows.map((row, ri) => (
                              <tr key={ri}>
                                {row.map((cell, ci) => (
                                  <td key={ci} style={{
                                    padding: '0.4rem 0.6rem',
                                    borderBottom: `1px solid ${T.border}`,
                                    fontWeight: ci === 1 ? 700 : 400,
                                    color: ci === 1 ? T.red : ci === 2 ? T.muted : T.text,
                                    whiteSpace: ci === 1 ? 'nowrap' : undefined,
                                    fontSize: ci === 2 ? '0.72rem' : undefined,
                                  }}>{cell}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Claude-style cost chart (complication variants) */}
                    {ex.bars && (() => {
                      const max = Math.max(...ex.bars.map(b => b.value));
                      return (
                        <div style={{ margin: '0.7rem 0 0.2rem', display: 'grid', gap: 8 }}>
                          {ex.bars.map((b, bi) => (
                            <div key={bi}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 3 }}>
                                <span style={{ color: T.text, fontWeight: 600 }}>{b.label}</span>
                                <span style={{ color: bi === 0 ? T.green : T.red, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                  {b.display}{b.delta ? ` (${b.delta})` : ''}
                                </span>
                              </div>
                              <div style={{ height: 10, borderRadius: 5, background: T.codeBg, overflow: 'hidden' }}>
                                <div style={{
                                  width: `${Math.round((b.value / max) * 100)}%`, height: '100%', borderRadius: 5,
                                  background: bi === 0
                                    ? `linear-gradient(90deg, ${T.navy}, ${T.gold})`
                                    : `linear-gradient(90deg, ${T.gold}, ${T.red})`,
                                }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {ex.note && (
                      <div style={{ marginTop: '0.6rem', fontSize: '0.8rem', color: T.muted, lineHeight: 1.6 }}>
                        {ex.note}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── SETUP ────────────────────────────────────────────────── */}
        <section style={{ marginBottom: 48 }}>
          <p style={eyebrow}>{'Set it up'}</p>
          <h2 style={h2}>{'Two minutes, three steps.'}</h2>

          <div className="grid sm:grid-cols-2 gap-4" style={{ marginTop: 20 }}>
            {/* Claude */}
            <div style={{ ...card, padding: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Sparkles size={16} color={T.gold} />
                <span style={{ fontFamily: sans, fontWeight: 700, fontSize: '0.95rem', color: T.text }}>Claude</span>
                <span style={{ fontFamily: sans, fontSize: '0.7rem', color: T.muted }}>{'web · mobile · desktop'}</span>
              </div>
              <ol style={{ margin: 0, paddingLeft: '1.15rem', fontFamily: sans, fontSize: '0.875rem', color: T.text, lineHeight: 2 }}>
                <li>{'Settings → Connectors'}</li>
                <li>{'"Add custom connector" → paste the URL above'}</li>
                <li>{'Ask: "what does an MRI cost near me?"'}</li>
              </ol>
            </div>

            {/* ChatGPT */}
            <div style={{ ...card, padding: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <MessageCircle size={16} color={T.gold} />
                <span style={{ fontFamily: sans, fontWeight: 700, fontSize: '0.95rem', color: T.text }}>ChatGPT</span>
                <span style={{ fontFamily: sans, fontSize: '0.7rem', color: T.muted }}>{'plans with connectors'}</span>
              </div>
              <ol style={{ margin: 0, paddingLeft: '1.15rem', fontFamily: sans, fontSize: '0.875rem', color: T.text, lineHeight: 2 }}>
                <li>{'Settings → Connectors → Advanced → Develop'}</li>
                <li>{'"Add custom connector" → paste the URL — no auth'}</li>
                <li>{'Ask away — same tools, same data'}</li>
              </ol>
            </div>
          </div>

          {/* Power users */}
          <div style={{ ...card, padding: '1.1rem 1.25rem', marginTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Terminal size={15} color={T.gold} />
              <span style={{ fontFamily: sans, fontWeight: 700, fontSize: '0.85rem', color: T.text }}>{'Power users'}</span>
            </div>
            <p style={{ fontFamily: sans, fontSize: '0.85rem', color: T.muted, margin: 0, lineHeight: 1.8 }}>
              {'Claude Code: '}
              <code style={{ fontFamily: mono, fontSize: '0.75rem', background: T.codeBg, padding: '0.15rem 0.45rem', borderRadius: 6, overflowWrap: 'anywhere' }}>
                {'claude mcp add --transport http ahp ' + MCP_URL}
              </code>
              <br />
              {'Analyst-grade workflows: '}
              <a href="/downloads/ahp-hospital-prices-skill.zip" download style={{ color: T.navy, textDecoration: 'underline' }}>
                {'download the price-analyst skill'}
              </a>
              {' (Claude → Settings → Capabilities → Skills). Full tool reference on the '}
              <Link to="/mcp" style={{ color: T.navy, textDecoration: 'underline' }}>{'developer page'}</Link>.
            </p>
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────── */}
        <section style={{ marginBottom: 48 }}>
          <p style={eyebrow}>{'Everything else you’d ask'}</p>
          <h2 style={h2}>{'The FAQ'}</h2>

          <div style={{ marginTop: 18, display: 'grid', gap: 8 }}>
            {FAQ.map((f, i) => {
              const open = openFaq === i;
              const advisor = brand.key === 'ahp' ? 'Dignity' : brand.shortName;
              const fq = f.q.replace(/\{advisor\}/g, advisor);
              const fa = f.a.replace(/\{advisor\}/g, advisor);
              return (
                <div key={i} style={{ ...card, overflow: 'hidden' }}>
                  <button
                    onClick={() => setOpenFaq(open ? null : i)}
                    aria-expanded={open}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 12, padding: '0.95rem 1.15rem', background: 'transparent', border: 'none',
                      cursor: 'pointer', textAlign: 'left',
                      fontFamily: sans, fontWeight: 600, fontSize: '0.92rem', color: T.text,
                    }}
                  >
                    {fq}
                    <ChevronDown
                      size={16}
                      color={T.gold}
                      style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s' }}
                    />
                  </button>
                  {open && (
                    <p style={{
                      fontFamily: sans, fontSize: '0.88rem', color: T.muted, lineHeight: 1.75,
                      padding: '0 1.15rem 1.05rem', margin: 0,
                    }}>
                      {fa}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* ── ON-SITE ALTERNATIVES ─────────────────────────────────── */}
        <section>
          <div style={{
            ...card, padding: '1.4rem 1.5rem',
            borderLeft: `4px solid ${T.gold}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Zap size={15} color={T.gold} />
              <span style={{ fontFamily: sans, fontWeight: 700, fontSize: '0.85rem', color: T.text }}>
                {'Prefer to stay on this site?'}
              </span>
            </div>
            <p style={{ fontFamily: sans, fontSize: '0.88rem', color: T.muted, lineHeight: 1.75, margin: 0 }}>
              {'Everything the connector reaches is browsable here too — free: the '}
              <Link to="/search" style={{ color: T.navy, textDecoration: 'underline' }}>{'price search'}</Link>
              {', the '}
              <Link to="/map" style={{ color: T.navy, textDecoration: 'underline' }}>{'hospital map'}</Link>
              {', 11,000+ '}
              <Link to="/scenarios" style={{ color: T.navy, textDecoration: 'underline' }}>{'priced scenarios'}</Link>
              {', and the printable Negotiation Sheet in the '}
              <Link to="/family" style={{ color: T.navy, textDecoration: 'underline' }}>{'family planner'}</Link>
              {'. And if you’d rather have an analyst do the whole thing: '}
              <Link to="/price-plan" style={{ color: T.navy, textDecoration: 'underline' }}>
                {'Build My Plan — $1.99'}
              </Link>
              {' verifies every code and prices your exact situation at every hospital near you.'}
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
